import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import type { GridColDef } from "@mui/x-data-grid";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import { LojaDataGrid } from "layouts/pages/loja/common";
import { setMessage, useMaterialUIController } from "context";
import { formatDateEndOfDay } from "helpers/formatDate";
import { IEventoResponse } from "interfaces/eventos";
import {
  convidarCompartilhamentoEvento,
  IEventoCompartilhamento,
  listarCompartilhamentosEvento,
  revogarCompartilhamentoEvento,
  atualizarCompartilhamentoEvento,
} from "services/eventoCompartilhamento";

import {
  COMPARTILHAR_ACCENT,
  compartilharFieldSx,
  compartilharSectionSx,
  compartilharToolbarSx,
} from "./compartilhar-evento.theme";

interface Props {
  evento: IEventoResponse;
}

const schemaConvite = Yup.object({
  email: Yup.string().email("E-mail inválido").required("Informe o e-mail"),
  exibirNomesAtletas: Yup.boolean(),
  permitirUploadFotos: Yup.boolean(),
});

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? COMPARTILHAR_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function ShareLinkInscrito({ evento }: Props) {
  const [, dispatch] = useMaterialUIController();
  const [lista, setLista] = useState<IEventoCompartilhamento[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarCompartilhamentosEvento(evento.id);
      setLista(data);
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error
            ? error.message
            : "Falha ao carregar compartilhamentos.",
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch, evento.id]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const ativos = lista.filter((item) => item.ativo).length;
    const revogados = lista.length - ativos;
    const comUpload = lista.filter(
      (item) => item.ativo && item.permitirUploadFotos,
    ).length;
    return { total: lista.length, ativos, revogados, comUpload };
  }, [lista]);

  const handleRevogar = useCallback(
    async (id: number) => {
      if (!window.confirm("Revogar o acesso desta pessoa a este evento?"))
        return;
      try {
        await revogarCompartilhamentoEvento(evento.id, id);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Acesso revogado.",
        });
        load();
      } catch (error: unknown) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: error instanceof Error ? error.message : "Falha ao revogar.",
        });
      }
    },
    [dispatch, evento.id, load],
  );

  const handleToggleNomes = useCallback(
    async (row: IEventoCompartilhamento) => {
      if (!row.ativo) return;
      try {
        await atualizarCompartilhamentoEvento(evento.id, row.id, {
          exibirNomesAtletas: !row.exibirNomesAtletas,
        });
        load();
      } catch (error: unknown) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: error instanceof Error ? error.message : "Falha ao atualizar.",
        });
      }
    },
    [dispatch, evento.id, load],
  );

  const handleToggleUpload = useCallback(
    async (row: IEventoCompartilhamento) => {
      if (!row.ativo) return;
      try {
        await atualizarCompartilhamentoEvento(evento.id, row.id, {
          permitirUploadFotos: !row.permitirUploadFotos,
        });
        load();
      } catch (error: unknown) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: error instanceof Error ? error.message : "Falha ao atualizar.",
        });
      }
    },
    [dispatch, evento.id, load],
  );

  const columns = useMemo((): GridColDef<IEventoCompartilhamento>[] => {
    return [
      {
        field: "email",
        headerName: "E-mail",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.row.email}
          </Typography>
        ),
      },
      {
        field: "exibirNomesAtletas",
        headerName: "Nomes",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Switch
            size="small"
            color="warning"
            checked={params.row.exibirNomesAtletas}
            disabled={!params.row.ativo}
            onChange={() => handleToggleNomes(params.row)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        field: "permitirUploadFotos",
        headerName: "Upload fotos",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Switch
            size="small"
            color="warning"
            checked={params.row.permitirUploadFotos}
            disabled={!params.row.ativo}
            onChange={() => handleToggleUpload(params.row)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        field: "ativo",
        headerName: "Status",
        width: 110,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.ativo ? "Ativo" : "Revogado"}
            color={params.row.ativo ? "success" : "default"}
            variant={params.row.ativo ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "convidadoEm",
        headerName: "Convidado em",
        width: 120,
        valueGetter: (params) =>
          formatDateEndOfDay(params.row.convidadoEm ?? ""),
      },
      {
        field: "ultimoAcessoEm",
        headerName: "Último acesso",
        width: 120,
        valueGetter: (params) =>
          params.row.ultimoAcessoEm
            ? formatDateEndOfDay(params.row.ultimoAcessoEm)
            : "—",
      },
      {
        field: "acoes",
        headerName: "",
        width: 52,
        align: "right",
        headerAlign: "right",
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.ativo ? (
            <IconButton
              color="error"
              size="small"
              title="Revogar acesso"
              onClick={(e) => {
                e.stopPropagation();
                handleRevogar(params.row.id);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          ) : null,
      },
    ];
  }, [handleToggleNomes, handleToggleUpload, handleRevogar]);

  return (
    <MDBox>
      <Box sx={compartilharToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ShareOutlinedIcon sx={{ color: COMPARTILHAR_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Compartilhar evento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {loading && lista.length === 0 ? (
        <Grid container spacing={2} mb={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={6} sm={4} key={i}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} mb={2.5}>
          <Grid item xs={6} sm={4}>
            <StatCard label="Total de convites" value={stats.total} accent />
          </Grid>
          <Grid item xs={6} sm={4}>
            <StatCard label="Acessos ativos" value={stats.ativos} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Revogados / com upload"
              value={`${stats.revogados} / ${stats.comUpload}`}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ ...compartilharSectionSx, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
          Novo convite
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Convide por e-mail. A pessoa precisa ter cadastro no site (ou criar
          conta) para ver inscrições sem dados sensíveis. O acesso é feito após
          login em &quot;Eventos compartilhados&quot; no site (LGPD).
        </Typography>

        <Formik
          initialValues={{
            email: "",
            exibirNomesAtletas: false,
            permitirUploadFotos: false,
          }}
          validationSchema={schemaConvite}
          onSubmit={async (values, { resetForm }) => {
            try {
              await convidarCompartilhamentoEvento(evento.id, {
                email: values.email.trim(),
                exibirNomesAtletas: values.exibirNomesAtletas,
                permitirUploadFotos: values.permitirUploadFotos,
              });
              setMessage(dispatch, {
                open: true,
                severity: "success",
                text: "Convite enviado por e-mail.",
              });
              resetForm();
              load();
            } catch (error: unknown) {
              setMessage(dispatch, {
                open: true,
                severity: "error",
                text:
                  error instanceof Error
                    ? error.message
                    : "Falha ao enviar convite.",
              });
            }
          }}
        >
          {({
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              {isSubmitting ? (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  icon={<CircularProgress size={18} />}
                >
                  Enviando e-mail de convite… Aguarde alguns instantes.
                </Alert>
              ) : null}

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                  <FormField
                    name="email"
                    label="E-mail do convidado"
                    type="email"
                    value={values.email}
                    disabled={isSubmitting}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue("email", e.target.value)
                    }
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    sx={compartilharFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="dark"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{
                      mt: { xs: 0, md: 1 },
                      background: `linear-gradient(195deg, ${COMPARTILHAR_ACCENT}, #e04a1a)`,
                    }}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <MailOutlineIcon />
                      )
                    }
                  >
                    {isSubmitting ? "Enviando…" : "Convidar"}
                  </MDButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    disabled={isSubmitting}
                    control={
                      <Switch
                        color="warning"
                        checked={values.exibirNomesAtletas}
                        disabled={isSubmitting}
                        onChange={(e) =>
                          setFieldValue("exibirNomesAtletas", e.target.checked)
                        }
                      />
                    }
                    label="Exibir nomes dos atletas"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Sem marcar: apenas números de inscrição e dados não
                    identificáveis.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    disabled={isSubmitting}
                    control={
                      <Switch
                        color="warning"
                        checked={values.permitirUploadFotos}
                        disabled={isSubmitting}
                        onChange={(e) =>
                          setFieldValue("permitirUploadFotos", e.target.checked)
                        }
                      />
                    }
                    label="Permitir upload de fotos"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    O convidado poderá enviar imagens para a galeria deste
                    evento.
                  </Typography>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>

      <Box sx={compartilharSectionSx}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Convites enviados
        </Typography>

        {!loading && lista.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
              bgcolor: "grey.50",
            }}
          >
            <ShareOutlinedIcon
              sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
            />
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Nenhum convite enviado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use o formulário acima para convidar alguém a acompanhar este
              evento.
            </Typography>
          </Box>
        ) : (
          <LojaDataGrid
            rows={lista}
            getRowId={(row) => row.id}
            columns={columns}
            loading={loading}
            autoHeight
            disableColumnMenu
            disableRowSelectionOnClick
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10, 25]}
            localeText={{
              noRowsLabel: "Nenhum convite enviado.",
            }}
            sx={{
              "& .MuiDataGrid-row:hover": {
                bgcolor: "rgba(249, 96, 48, 0.04)",
              },
            }}
          />
        )}
      </Box>
    </MDBox>
  );
}
